import { EventType } from "@/analytics/analytics-events";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { useNavigateToDraftView } from "@/utils/redirects";
import {
  fetchDataInInterval,
  projectSettingsSelector,
  setPublishSettings,
  useStagingAreaApiClientWithoutContext,
} from "@faro-lotv/data-management";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  ApiResponseError,
  CaptureApiClient,
  CaptureTreeEntity,
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
  CaptureTreePointCloudType,
  isDeletedRevisionStatus,
  isRevisionScanEntity,
  ProjectApi,
  RegistrationEdgeRevision,
  RegistrationEdgeType,
  RegistrationRevision,
  RegistrationState,
  RegistrationStatus,
  RevisionStatus,
  RevisionType,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, useState } from "react";
import { applyRevision } from "../editing/apply-revision";
import { useIsDataPreparationReadOnly } from "../hooks/use-is-data-preparation-read-only";
import { loadRevisionDataThunk } from "../loading/load-revision-data-thunk";
import { clearUserEdits } from "../store/data-preparation-actions";
import { selectInvalidRevisionScans } from "../store/data-preparation-ui/data-preparation-ui-selectors";
import {
  selectRegistrationAlgorithm,
  selectRegistrationAlgorithmSettings,
} from "../store/registration-parameters/registration-parameters-selectors";
import { selectRevisionRegistrationEdges } from "../store/revision/revision-edge-selectors";
import {
  selectRevisionEntities,
  UpdatedRevisionEntities,
} from "../store/revision/revision-entity-selectors";
import {
  selectIsRevisionUserModified,
  selectUpdatedRevisionEdges,
  selectUpdatedRevisionEntities,
} from "../store/user-edits/data-preparation-user-edit-selectors";

/** Registration statuses indicating the project is fully connected and stable. */
const FINAL_REGISTRATION_STATUSES: readonly RegistrationStatus[] = [
  RegistrationStatus.registered,
  RegistrationStatus.lockedManually,
];

const FORBIDDEN_ERROR_MESSAGE = `You can't edit this project for a few reasons:
  You may lack permission.
  The project might be archived.
  The workspace subscription could be expired.`;

const HTTP_STATUS_FORBIDDEN = 403;

/**
 * @param edges The registration edges of the draft revision.
 * @returns `true` if the draft contains at least one candidate or anti-candidate edge with status `added`.
 */
function hasUserDefinedEdges(edges: RegistrationEdgeRevision[]): boolean {
  return edges.some(
    (edge) =>
      edge.status === RevisionStatus.added &&
      (edge.type === RegistrationEdgeType.candidate ||
        edge.type === RegistrationEdgeType.antiCandidate),
  );
}

/**
 * Evaluate whether a registration could benefit from being refined before publishing.
 *
 * @param entities The capture tree entities of the draft revision.
 * @param edges The registration edges of the draft revision.
 * @returns `true` if the user should be offered to refine before publishing.
 */
export function isRefinementSuggested(
  entities: CaptureTreeEntityRevision[],
  edges: RegistrationEdgeRevision[],
): boolean {
  const rootEntity = entities.find(
    (entity) => entity.type === CaptureTreeEntityType.root,
  );
  const rootStatus =
    rootEntity?.registrationStatus ?? RegistrationStatus.unknown;
  if (!FINAL_REGISTRATION_STATUSES.includes(rootStatus)) {
    return true;
  }

  if (hasUserDefinedEdges(edges)) {
    return true;
  }

  const hasDeletedScan = entities.some(
    (entity) =>
      isRevisionScanEntity(entity) && isDeletedRevisionStatus(entity.status),
  );
  if (hasDeletedScan) {
    return true;
  }

  return false;
}

type UseRefineRegistrationResult = {
  isEnabled: boolean;
  isRefinementRunning: boolean;
  refine(): Promise<void>;
};

/**
 * @param revision The active registration revision, if present.
 * @param saveUserEdits save implementation to run before refinement.
 * @returns The shared refine registration action used by both refine buttons.
 */
export function useRefineRegistration(
  revision: RegistrationRevision | undefined,
  saveUserEdits: (
    currentRevision: RegistrationRevision,
  ) => Promise<RegistrationRevision>,
): UseRefineRegistrationResult {
  const navigateToDraftView = useNavigateToDraftView();
  const [isRefinementRunning, setIsRefinementRunning] = useState(false);

  const { registrationApiClient, projectApiClient, settingsApiClient } =
    useApiClientContext();
  const stagingAreaApiClient = useStagingAreaApiClientWithoutContext({
    projectId: projectApiClient.projectId,
  });
  const { dispatch } = useAppStore();
  const { handleErrorWithToast } = useErrorHandlers();

  const isRevisionUserModified = useAppSelector(selectIsRevisionUserModified);

  const isEnabled =
    !!revision &&
    (revision.state === RegistrationState.started || isRevisionUserModified);

  const hasRegistrationDevFeature = useAppSelector(
    selectHasFeature(Features.RegistrationDev),
  );

  const registrationAlgorithm = useAppSelector(selectRegistrationAlgorithm);
  const registrationSettings = useAppSelector(
    selectRegistrationAlgorithmSettings,
  );

  const { workflowStopAfter } = useAppSelector(projectSettingsSelector);

  const runRefinement = useCallback(async () => {
    try {
      await registrationApiClient?.startCaptureTreeRegistration({
        forceGraphOptimizer: true,
        devOverrides: hasRegistrationDevFeature
          ? {
              algorithm: registrationAlgorithm,
              parameters: registrationSettings?.parameters,
            }
          : undefined,
      });

      // Refetch data after changes, so that Draft View displays the registration state.
      await Promise.all([
        fetchDataInInterval(
          dispatch,
          projectApiClient.projectId,
          stagingAreaApiClient,
        ),
        dispatch(loadRevisionDataThunk({ projectApiClient })),
      ]);

      navigateToDraftView(projectApiClient.projectId, "refineRegistration");
    } catch (error) {
      handleErrorWithToast({
        error,
        title: "Failed to Run Refinement",
      });
    }
  }, [
    dispatch,
    handleErrorWithToast,
    hasRegistrationDevFeature,
    navigateToDraftView,
    projectApiClient,
    registrationAlgorithm,
    registrationApiClient,
    registrationSettings?.parameters,
    stagingAreaApiClient,
  ]);

  const refine = useCallback(async () => {
    if (!revision) {
      return;
    }

    Analytics.track(EventType.refineAndPublish);

    setIsRefinementRunning(true);

    try {
      await saveUserEdits(revision);
    } catch (error: unknown) {
      handle403AwareError(error, handleErrorWithToast);
      setIsRefinementRunning(false);
      return;
    }

    // Don't publish automatically, allow the user to review the results.
    await setPublishSettings(
      dispatch,
      projectApiClient.projectId,
      settingsApiClient,
      handleErrorWithToast,
      { workflowStopAfter, valueWorkflowStopAfter: "register" },
    );

    // Clear the user edits, since they are now persisted in the edited revision
    dispatch(clearUserEdits());

    try {
      await runRefinement();
    } catch (error) {
      handleErrorWithToast({
        error,
        title: "Failed to run refinement",
      });
    }

    setIsRefinementRunning(false);
  }, [
    dispatch,
    handleErrorWithToast,
    projectApiClient.projectId,
    revision,
    runRefinement,
    saveUserEdits,
    settingsApiClient,
    workflowStopAfter,
  ]);

  return {
    isEnabled,
    isRefinementRunning,
    refine,
  };
}

type UseSaveUserEditsWithDialogResult = {
  isSavingUserEdits: boolean;
  saveUserEdits(
    currentRevision: RegistrationRevision,
  ): Promise<RegistrationRevision>;
};

/**
 * A hook to save the user edits to the project API.
 *
 * @returns A callback to save the user edits and the saving state
 */
export function useSaveUserEdits(): UseSaveUserEditsWithDialogResult {
  const isReadOnly = useIsDataPreparationReadOnly();
  const [isSavingUserEdits, setIsSavingUserEdits] = useState(false);
  const { getState } = useAppStore();
  const { projectApiClient } = useApiClientContext();
  const dispatch = useAppDispatch();

  const saveUserEdits = useCallback(
    async (
      currentRevision: RegistrationRevision,
    ): Promise<RegistrationRevision> => {
      assert(
        !isReadOnly,
        "Editing is disabled when viewing a specific revision.",
      );
      setIsSavingUserEdits(true);
      try {
        const editRevision = await revisionToEdit(
          currentRevision,
          projectApiClient,
          getState(),
        );

        const updatedEntities = selectUpdatedRevisionEntities(getState());
        await persistUserEdits(
          projectApiClient,
          editRevision.id,
          updatedEntities,
        );

        const updatedEdges = selectUpdatedRevisionEdges(getState());

        if (updatedEdges.newEdges.length) {
          await projectApiClient.createOrUpdateRegistrationEdges({
            registrationRevisionId: editRevision.id,
            requestBody: updatedEdges.newEdges,
          });
        }

        if (updatedEdges.deletedEdges.size) {
          await projectApiClient.markRegistrationEdgeRevisionsAsDeleted(
            editRevision.id,
            Array.from(updatedEdges.deletedEdges),
          );
        }

        const deletedScanIds = selectInvalidRevisionScans(getState())
          .filter((scan) => !isDeletedRevisionStatus(scan.status))
          .map((scan) => scan.id);
        if (deletedScanIds.length) {
          await projectApiClient.markCaptureTreeEntityRevisionsAsDeleted({
            registrationRevisionId: editRevision.id,
            entityIds: deletedScanIds,
          });
        }

        const newRevision = await applyRevision({
          revision: editRevision,
          projectApiClient,
        });
        dispatch(clearUserEdits());
        return newRevision;
      } finally {
        setIsSavingUserEdits(false);
      }
    },
    [isReadOnly, projectApiClient, getState, dispatch],
  );

  return {
    isSavingUserEdits,
    saveUserEdits,
  };
}

/**
 * Persist the user edits to the project API.
 *
 * @param projectApiClient The API client to use.
 * @param registrationRevisionId The ID of the revision that needs to be updated.
 * @param updatedEntities The entities that need to be updated.
 */
async function persistUserEdits(
  projectApiClient: ProjectApi,
  registrationRevisionId: GUID,
  updatedEntities: UpdatedRevisionEntities,
): Promise<void> {
  const { updatedRoot, updatedClusters, updatedScans } = updatedEntities;

  // Update the entities on the API
  if (updatedRoot) {
    await projectApiClient.createOrUpdateRootEntityForRegistrationRevision({
      registrationRevisionId,
      requestBody: updatedRoot,
    });
  }

  if (updatedClusters.length) {
    await projectApiClient.createOrUpdateClusterEntitiesForRegistrationRevision(
      {
        registrationRevisionId,
        requestBody: updatedClusters,
      },
    );
  }

  if (updatedScans.length) {
    await projectApiClient.createOrUpdateScanEntitiesForRegistrationRevision({
      registrationRevisionId,
      requestBody: updatedScans,
    });
  }
}

/** The types of point cloud files that the registration backend can process. */
const SUPPORTED_POINT_CLOUD_TYPES = [
  CaptureTreePointCloudType.e57,
  CaptureTreePointCloudType.laz,
  CaptureTreePointCloudType.cpe,
];

/**
 * @param entity The entity to check.
 * @returns Whether the entity is supported by the cloud registration service.
 *    In particular, scans must have at least one supported point cloud.
 *    This ensures we can register all scans in the revision and display them in the UI.
 */
function isEntitySupportedByCloudRegistration(
  entity: CaptureTreeEntity,
): boolean {
  return (
    !entity.pointClouds ||
    entity.pointClouds.some((pc) =>
      SUPPORTED_POINT_CLOUD_TYPES.includes(pc.type),
    )
  );
}

/**
 * Determine the revision to apply the user edits to.
 *
 * @param currentRevision The current revision to base the new revision on.
 * @param projectApiClient The Project API client.
 * @param state The current state of the application.
 * @returns The newly created revision.
 */
export async function revisionToEdit(
  currentRevision: RegistrationRevision,
  projectApiClient: ProjectApi,
  state: RootState,
): Promise<RegistrationRevision> {
  // Data from the current revision, should overwrite data from the main revision
  const currentEntities = selectRevisionEntities(state)
    .filter(
      // In most cases, the current entities were already filtered when the revision was created
      // But when they are not, it could cause entities from the main revision to be "added" again, which gives errors in the Project API
      // Hence, they need to be filtered here to prevent these errors
      isEntitySupportedByCloudRegistration,
    )
    .filter((entity) => !isDeletedRevisionStatus(entity.status));

  const currentEdgesWithoutDeleted = selectRevisionRegistrationEdges(
    state,
  ).filter(
    (edge) =>
      // Prevent issues when adding edges, that where already removed from the project.
      // Newly deleted edges are deleted in `saveUserEdits`;
      !isDeletedRevisionStatus(edge.status),
  );

  if (currentRevision.revisionType === RevisionType.draft) {
    // If the current revision is a draft, directly create a new revision
    // NOTE: For the normal user workflow, this is the expected path
    return projectApiClient.createRegistrationRevision(
      currentEntities.map((e) => e.id),
      currentEdgesWithoutDeleted.map((e) => e.id),
      CaptureApiClient.registrationFrontend,
    );
  }

  // If a specific revision is specified, merge in the data from the main revision to ensure it's up-to-date and to avoid conflicts
  // This scenario should only happen for dev workflows

  // Data from the main revision, to include new entities and edges
  const mainEntities = (await projectApiClient.getCaptureTree()).filter(
    // Only show entities that the backend can process, to simplify the view
    // When the backend automatically creates revisions, this is also done
    isEntitySupportedByCloudRegistration,
  );
  const mainEntityIds = mainEntities.map((e) => e.id);
  const mainEdges = await projectApiClient.getRegistrationEdges();
  const mainEdgeIds = mainEdges.map((e) => e.id);

  // Prepare the new revision
  const newRevision = await projectApiClient.createRegistrationRevision(
    mainEntityIds,
    mainEdgeIds,
    CaptureApiClient.registrationFrontend,
  );

  // In this case we want to delete all existing edges in the target revision and use only the ones that are in the current version
  await projectApiClient.markRegistrationEdgeRevisionsAsDeleted(
    newRevision.id,
    mainEdgeIds,
  );

  await projectApiClient.mergeRegistrationRevisionData(
    currentEntities,
    currentEdgesWithoutDeleted,
    mainEntities,
    newRevision.id,
  );

  return newRevision;
}

function errorIncludes403(err: unknown): boolean {
  if (err instanceof ApiResponseError) {
    return err.status === HTTP_STATUS_FORBIDDEN;
  }
  return false;
}

/**
 * Show an error toast for a failed save, with a dedicated message for 403 (forbidden) responses.
 *
 * @param error The error thrown while saving the user edits.
 * @param handleErrorWithToast Callback used to display the error toast to the user.
 */
export function handle403AwareError(
  error: unknown,
  handleErrorWithToast: (args: { error: unknown; title: string }) => void,
): void {
  if (errorIncludes403(error)) {
    handleErrorWithToast({
      error: new Error(FORBIDDEN_ERROR_MESSAGE),
      title: "Access Denied",
    });
  } else {
    handleErrorWithToast({
      error,
      title: "Failed to save edited registration",
    });
  }
}
